export function calculateNextPaymentDate(startDate: string, pattern: number): string {
  // Use UTC dates to avoid timezone issues
  const date = new Date(startDate + 'T00:00:00.000Z');
  date.setUTCMonth(date.getUTCMonth() + pattern);
  return date.toISOString().split('T')[0]; // Return YYYY-MM-DD format
}

// Simple approach: Calculate next payment based purely on entry date + payment pattern
// Ignore timing of payment (early/late) - just determine which payment cycle we're in
export function calculateNextDueDateAnchored(startDate: string, patternMonths: number, afterDate?: string): string {
  if (!startDate || !patternMonths) return '';
  
  const entryDate = new Date(startDate + 'T00:00:00.000Z');
  const entryDay = entryDate.getUTCDate();
  const entryMonth = entryDate.getUTCMonth();
  const entryYear = entryDate.getUTCFullYear();

  // Helper to create a due date with proper month-end handling
  const createDueDate = (year: number, month: number, day: number) => {
    const date = new Date(Date.UTC(year, month, day));
    if (date.getUTCDate() !== day) {
      // Handle month overflow (e.g., Feb 31 -> Feb 28/29)
      date.setUTCDate(0); // Go to last day of previous month
    }
    return date;
  };

  if (!afterDate) {
    // No payment date provided - return first due date
    const firstDue = createDueDate(entryYear, entryMonth + patternMonths, entryDay);
    return firstDue.toISOString().split('T')[0];
  }

  const paymentDate = new Date(afterDate + 'T00:00:00.000Z');
  const paymentYear = paymentDate.getUTCFullYear();

  // Calculate next due date based on payment year and pattern
  if (patternMonths >= 12) {
    // Yearly payments: next due is always (entry anniversary + pattern months) in year after payment
    const nextDueYear = paymentYear + Math.floor(patternMonths / 12);
    const nextDue = createDueDate(nextDueYear, entryMonth, entryDay);
    return nextDue.toISOString().split('T')[0];
  } else {
    // Monthly payments: calculate which cycle we're in and return next cycle
    const paymentMonth = paymentDate.getUTCMonth();
    
    // Calculate months since entry
    let monthsSinceEntry = (paymentYear - entryYear) * 12 + (paymentMonth - entryMonth);
    
    // Always advance to next cycle - no matter when payment is made
    const currentCycleCount = Math.floor(monthsSinceEntry / patternMonths);
    const nextCycleCount = currentCycleCount + 1;
    const nextCycleMonths = nextCycleCount * patternMonths;
    
    // Calculate next due date
    const nextDueYear = entryYear + Math.floor((entryMonth + nextCycleMonths) / 12);
    const nextDueMonth = (entryMonth + nextCycleMonths) % 12;
    const nextDue = createDueDate(nextDueYear, nextDueMonth, entryDay);
    
    return nextDue.toISOString().split('T')[0];
  }
}

export function isDatePastDue(dateStr: string): boolean {
  if (!dateStr) return false;
  
  const date = new Date(dateStr);
  const now = new Date();
  
  // Set both dates to midnight for consistent comparison
  date.setHours(0, 0, 0, 0);
  now.setHours(0, 0, 0, 0);
  
  return date < now;
}

export function generatePaymentPeriodId(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}
